import React, { useState, useRef } from "react";
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";

// Import assets
import { Images } from "../../../utils/Images";
import { getImageSrc } from "../../../utils/imageMapping";

// Styled components
const Container = styled(Box)(({ theme, isMobile }) => ({
  background: "#FFFFFF",
  borderRadius: isMobile ? "0px" : "12px",
  border: isMobile ? "none" : "1px solid #F1F1F1",
  padding: isMobile ? "0px" : "30px 30px 7px 30px",
  width: "100%",
}));

const HeaderContainer = styled(Box)(({ theme, isMobile }) => ({
  display: "flex",
  alignItems: "center",
  gap: "10px",
  marginBottom: "20px",
}));

const HeaderBorder = styled(Box)(({ theme, isMobile }) => ({
  height: isMobile ? "0.25px" : "0.51px",
  background: "#B9B9B8",
  marginBottom: isMobile ? "10px" : "20px",
}));

const StarContainer = styled(Box)(({ theme, isMobile }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& img": {
    width: isMobile ? "14px" : "24px",
    height: isMobile ? "14px" : "24px",
  },
}));

const ProjectsTitle = styled(Typography)(({ theme, isMobile }) => ({
  // fontFamily: "Red Hat Display",
  fontWeight: 700,
  fontStyle: "Bold",
  fontSize: isMobile ? "14px" : "24px",
  lineHeight: isMobile ? "14px" : "24px",
  letterSpacing: "-2%",
  verticalAlign: "middle",
  color: "#1D1D1B",
}));

const ImageContainer = styled(Box)(({ theme, isMobile }) => ({
  height: isMobile ? "auto" : "559px",
  borderRadius: "10px",
  marginTop: isMobile ? "10px" : "30px",
  position: "relative",
  overflow: isMobile ? "visible" : "hidden",
  background: "#F5F5F5",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  "& img, & video": {
    width: "100%",
    height: isMobile ? "auto" : "100%",
    objectFit: "cover",
    borderRadius: isMobile ? "10px 10px 0 0" : "10px",
    maxHeight: isMobile ? "none" : "100%",
    display: "block",
  },
  "@media (min-width: 1440px)": {
    height: "auto",
    minHeight: "559px",
    "& img, & video": {
      objectFit: "contain",
      width: "100%",
      height: "auto",
      maxHeight: "800px",
    },
  },
}));

const ImageWrapper = styled(Box)(({ theme, isMobile }) => ({
  width: "100%",
  position: "relative",
  height: isMobile ? "auto" : "auto",
  aspectRatio: isMobile ? "16/9" : "auto",
  "& img, & video": {
    width: "100%",
    height: isMobile ? "100%" : "auto",
    objectFit: "cover",
    display: "block",
  },
  "&::after": isMobile ? {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "10%",
    background: "linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.6) 50%, rgba(0,0,0,0) 100%)",
    pointerEvents: "none",
    zIndex: 1,
  } : {},
}));

const TextOverlay = styled(Box)(({ theme, isMobile }) => ({
  position: isMobile ? "relative" : "absolute",
  bottom: isMobile ? "auto" : 0,
  left: 0,
  right: 0,
  background: "#00000080",
  backdropFilter: "blur(50px)",
  padding: isMobile ? "16px 14px" : "20px 20px 24px 20px",
  borderRadius: isMobile ? "0 0 10px 10px" : "0 0 10px 10px",
  marginTop: isMobile ? "-10%" : 0,
  zIndex: isMobile ? 2 : "auto",
  width: "100%",
}));

const OverlayTitle = styled(Typography)(({ theme, isMobile }) => ({
  // fontFamily: "Red Hat Display",
  fontWeight: 700,
  fontStyle: "Bold",
  fontSize: isMobile ? "14px" : "22px",
  lineHeight: isMobile ? "17px" : "32.36px",
  letterSpacing: "0%",
  verticalAlign: "middle",
  color: "#FFFFFF",
  marginBottom: isMobile ? "10px" : "16px",
}));

const OverlayDescription = styled(Typography)(({ theme, isMobile }) => ({
  // fontFamily: isMobile ? "Red Hat Display" : "inherit",
  fontWeight: 400,
  fontStyle: "Regular",
  fontSize: isMobile ? "12px" : "14px",
  lineHeight: isMobile ? "19px" : "24px",
  letterSpacing: "0%",
  textAlign: "justify",
  color: "#E8E8E8",
  "& *": {
    fontFamily: "inherit",
    // fontWeight: "inherit",
    // fontStyle: "inherit",
    fontSize: "inherit",
    lineHeight: "inherit",
    letterSpacing: "inherit",
    color: "inherit",
  },
}));

const BulletPoint = styled(Typography)(({ theme, isMobile }) => ({
  fontFamily: isMobile ? "Red Hat Display" : "inherit",
  fontWeight: 400,
  fontStyle: "Regular",
  fontSize: isMobile ? "12px" : "14px",
  lineHeight: isMobile ? "19px" : "24px",
  letterSpacing: "0%",
  textAlign: "justify",
  color: "#E8E8E8",
  marginTop: isMobile ? "4px" : "8px",
  "&::before": {
    content: '"• "',
    color: "#E8E8E8",
  },
}));

const Projects = ({ campaignData }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:600px)');
  const [videoStarted, setVideoStarted] = useState({});
  const videoRefs = useRef({});

  // Don't render if no projects
  if (!campaignData?.projects || !Array.isArray(campaignData.projects) || campaignData.projects.length === 0) {
    return null;
  }

  // Helper function to check if media is a video
  const isVideo = (project) => {
    if (!project) return false;
    
    // Check if there's an explicit type field
    if (project.type === 'video' || project.mediaType === 'video') {
      return true;
    }
    
    // Check URL for video extensions
    const url = project.image || project.media || '';
    if (!url) return false;
    
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv', '.m4v'];
    const urlLower = url.toLowerCase();
    const hasVideoExtension = videoExtensions.some(ext => urlLower.includes(ext));
    
    console.log('Checking if video:', { url, hasVideoExtension, projectType: project.type });
    return hasVideoExtension;
  };

  // Helper function to get media source
  const getProjectMediaSrc = (project) => {
    const mediaPath = project.image || project.media || project.video || '';
    
    console.log('Getting media source:', { mediaPath, project });
    
    // For full URLs, return as-is
    if (mediaPath && (mediaPath.startsWith('http://') || mediaPath.startsWith('https://'))) {
      return mediaPath;
    }
    // For local paths, use imageMapping utility
    return getImageSrc(mediaPath);
  };

  return (
    <Container isMobile={isMobile}>
      {/* Header Section */}
      <HeaderContainer isMobile={isMobile}>
        <StarContainer isMobile={isMobile}>
          <img src={Images.streamlineSharpStar} alt="Star" />
        </StarContainer>
        <ProjectsTitle isMobile={isMobile}>
          Project
        </ProjectsTitle>
      </HeaderContainer>
      <HeaderBorder isMobile={isMobile} />

      {/* Projects List */}
      {campaignData?.projects?.map((project, index) => (
        <Box 
          key={project.id} 
          sx={{ 
            marginBottom: isMobile ? "20px" : "0px",
            borderRadius: "10px",
            overflow: "hidden",
          }}
        >
          <ImageContainer isMobile={isMobile}>
            {isMobile ? (
              <>
                <ImageWrapper isMobile={isMobile}>
                  {isVideo(project) ? (
                    <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
                      <video
                        ref={(el) => { if (el) videoRefs.current[project.id] = el; }}
                        src={getProjectMediaSrc(project)}
                        controls
                        playsInline
                        onPlay={() => setVideoStarted((prev) => ({ ...prev, [project.id]: true }))}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                      {!videoStarted[project.id] && (
                        <Box
                          onClick={() => { const v = videoRefs.current[project.id]; if (v) v.play(); }}
                          sx={{
                            position: "absolute",
                            top: 0, left: 0, right: 0, bottom: 0,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            cursor: "pointer",
                            background: "rgba(0,0,0,0.35)",
                            "&:hover": { background: "rgba(0,0,0,0.45)" },
                          }}
                          aria-label="Play video"
                        >
                          <Box sx={{
                            width: 56, height: 56, borderRadius: "50%",
                            background: "rgba(255,255,255,0.95)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
                          }}>
                            <svg width={24} height={24} viewBox="0 0 24 24" fill="none"><path d="M8 5v14l11-7L8 5z" fill="#1D1D1B" /></svg>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <img
                      src={getProjectMediaSrc(project)}
                      alt={`Project ${project.id}`}
                    />
                  )}
                </ImageWrapper>
                <TextOverlay isMobile={isMobile}>
                  {project.title && (
                    <OverlayTitle isMobile={isMobile}>
                      {project.title}
                    </OverlayTitle>
                  )}
                  <OverlayDescription 
                    isMobile={isMobile}
                    dangerouslySetInnerHTML={{ __html: project.description || '' }}
                  />
                  {project.bulletPoints && project.bulletPoints.map((point, pointIndex) => (
                    <BulletPoint key={pointIndex} isMobile={isMobile}>
                      {point}
                    </BulletPoint>
                  ))}
                </TextOverlay>
              </>
            ) : (
              <>
                {isVideo(project) ? (
                  <Box sx={{ position: "relative", width: "100%", height: "100%", minHeight: "100%" }}>
                    <video
                      ref={(el) => { if (el) videoRefs.current[project.id] = el; }}
                      src={getProjectMediaSrc(project)}
                      controls
                      playsInline
                      onPlay={() => setVideoStarted((prev) => ({ ...prev, [project.id]: true }))}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                    {!videoStarted[project.id] && (
                      <Box
                        onClick={() => { const v = videoRefs.current[project.id]; if (v) v.play(); }}
                        sx={{
                          position: "absolute",
                          top: 0, left: 0, right: 0, bottom: 0,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          cursor: "pointer",
                          background: "rgba(0,0,0,0.35)",
                          "&:hover": { background: "rgba(0,0,0,0.45)" },
                        }}
                        aria-label="Play video"
                      >
                        <Box sx={{
                          width: 72, height: 72, borderRadius: "50%",
                          background: "rgba(255,255,255,0.95)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
                        }}>
                          <svg width={32} height={32} viewBox="0 0 24 24" fill="none"><path d="M8 5v14l11-7L8 5z" fill="#1D1D1B" /></svg>
                        </Box>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <img
                    src={getProjectMediaSrc(project)}
                    alt={`Project ${project.id}`}
                  />
                )}
                <TextOverlay isMobile={isMobile}>
                  {project.title && (
                    <OverlayTitle isMobile={isMobile}>
                      {project.title}
                    </OverlayTitle>
                  )}
                  <OverlayDescription 
                    isMobile={isMobile}
                    dangerouslySetInnerHTML={{ __html: project.description || '' }}
                  />
                  {project.bulletPoints && project.bulletPoints.map((point, pointIndex) => (
                    <BulletPoint key={pointIndex} isMobile={isMobile}>
                      {point}
                    </BulletPoint>
                  ))}
                </TextOverlay>
              </>
            )}
          </ImageContainer>
        </Box>
      ))}
    </Container>
  );
};

export default Projects;
